import {Media, Story} from '../../../schema';
import {Fragment, useState} from 'react';
import {format} from 'date-fns';
import {de, enUS} from 'date-fns/locale';
import {Menu, Transition} from '@headlessui/react';
import {EllipsisVerticalIcon} from '@heroicons/react/24/solid';
import {Button} from '../../../components/Button';
import config from '../../../config';

export interface StoryElementProps {
  story: Story;
  onEdit: () => void;
  deleteStory: (storyId: string) => void;
}

const mediaUrl = (tripId: string, media: Media) =>
  `${config.media.cdnUrl}/${tripId}/${media.id}-thumbnail.${media.ext}`;

export const StoryElement = (props: StoryElementProps) => {
  const [locale] = useState(navigator.language);
  const [showTime, setShowTime] = useState(false);

  const date = locale.startsWith('de')
    ? format(new Date(props.story.date), 'd. MMMM yyyy', {
        locale: de,
      })
    : format(new Date(props.story.date), 'MMMM d, yyyy', {
        locale: enUS,
      });

  const time = locale.startsWith('de')
    ? format(new Date(props.story.date), 'p', {
        locale: de,
      })
    : format(new Date(props.story.date), 'p', {
        locale: enUS,
      });

  return (
    <div className="relative">
      <Menu as="div" className="absolute top-2 right-2">
        <Menu.Button className="p-2 rounded-full hover:bg-muted">
          <EllipsisVerticalIcon className="w-6 h-6" />
        </Menu.Button>
        <Transition
          as={Fragment}
          enter="transition ease-out duration-100"
          enterFrom="transform opacity-0 scale-95"
          enterTo="transform opacity-100 scale-100"
          leave="transition ease-in duration-75"
          leaveFrom="transform opacity-100 scale-100"
          leaveTo="transform opacity-0 scale-95"
        >
          <Menu.Items className="absolute right-0 z-10 mt-2 w-48 origin-top-right rounded-md bg-surface py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
            <Menu.Item>
              {({ active }) => (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={props.onEdit}
                  className={`w-full justify-start rounded-none px-4 text-main`}
                >
                  Edit Story
                </Button>
              )}
            </Menu.Item>
          </Menu.Items>
        </Transition>
      </Menu>
      <div className="flex flex-col items-center mb-4">
        <div className="flex justify-center items-center space-x-2 text-sm">
          <div className="group relative">
            <p onClick={() => setShowTime(!showTime)}>{date}</p>
            {/* Desktop tooltip - shows on hover */}
            <div className="hidden md:group-hover:block absolute left-1/2 -translate-x-1/2 -top-8 px-2 py-1 bg-surface border border-subtle rounded text-xs text-muted whitespace-nowrap shadow-sm">
              {time}
            </div>
          </div>
          {props.story.place && <span>&bull;</span>}
          {props.story.place && <p>{props.story.place}</p>}
        </div>
        {/* Mobile time - shows on tap */}
        {showTime && <div className="md:hidden text-xs text-muted mt-1">{time}</div>}
      </div>
      <h2 className="mt-1 text-4xl/9 text-center">{props.story.title}</h2>
      <p className="mt-4 text-lg text-center whitespace-pre-line">{props.story.description}</p>
      <div className="mt-6 flex flex-wrap gap-1 justify-center">
        {props.story.medias.map((media: Media) => (
          <img
            key={media.id}
            className="max-h-[200px] w-auto sm:max-w-none max-w-[40vw] rounded-xl object-cover"
            src={mediaUrl(props.story.tripId, media)}
          />
        ))}
      </div>
    </div>
  );
};
